import { useCallback, useEffect, useState } from 'react';
import {
  ApiError, fmt, superadminApi,
  type CreditBalanceDto, type CreditLedgerEntryDto, type CreditTopupDto,
  type PlatformCreditOverviewDto,
} from '../api';

/**
 * TICK-100 — platform credit administration.
 *
 * Every write on this screen creates money-equivalent value out of nothing, which makes it the most
 * abusable surface in the feature. Each one demands a reason before the button enables, and each
 * one lands in `audit_log` and in the owner's own `credit_ledger` with the acting identity — so
 * "who gave this organizer 5,000 credits, and why" is always answerable.
 */

const TOPUP_STATUS: Record<CreditTopupDto['status'], string> = {
  pending: 'รอแนบสลิป',
  awaiting_verification: 'รอตรวจสอบ',
  confirmed: 'ยืนยันแล้ว',
  rejected: 'ปฏิเสธแล้ว',
  expired: 'หมดอายุ',
};

type OwnerDetail = CreditBalanceDto & { ledger: CreditLedgerEntryDto[]; topups: CreditTopupDto[] };

export function SuperadminCreditsPanel({ token }: { token: string }) {
  const [data, setData] = useState<PlatformCreditOverviewDto | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<OwnerDetail | null>(null);

  const [grant, setGrant] = useState({ tierId: '', credits: '', validDays: '', unitPriceThb: '', note: '' });
  const [debt, setDebt] = useState({ delta: '', reason: '' });
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setData(await superadminApi.getCreditOverview(token));
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'โหลดข้อมูลเครดิตไม่สำเร็จ');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openOwner = async (ownerEmail: string) => {
    if (selected === ownerEmail) { setSelected(null); setDetail(null); return; }
    setSelected(ownerEmail);
    setDetail(null);
    try {
      setDetail(await superadminApi.getOwnerCredits(token, ownerEmail));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'โหลดข้อมูลผู้จัดงานไม่สำเร็จ');
    }
  };

  const refreshAll = async () => {
    await load();
    if (selected) setDetail(await superadminApi.getOwnerCredits(token, selected));
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await refreshAll();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ดำเนินการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  // A grant needs a reason, plus either a tier or a full custom spec. Enforced here so the button
  // is honest about it, and again on the server, which is where it counts.
  const grantReady = !!selected
    && grant.note.trim().length > 0
    && (!!grant.tierId || (Number(grant.credits) > 0 && Number(grant.validDays) > 0));

  if (error && !data) return <div className="form-error">{error}</div>;
  if (!data) return <div className="admin-group">กำลังโหลด…</div>;

  return (
    <div>
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="admin-group">
        <div className="card-step">ภาพรวมเครดิตทั้งแพลตฟอร์ม</div>
        <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginTop: 8 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{data.totalSpendable.toLocaleString('en-US')}</div>
            <div style={{ fontSize: 13, opacity: 0.7 }}>เครดิตคงเหลือรวม</div>
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{data.totalDebt.toLocaleString('en-US')}</div>
            <div style={{ fontSize: 13, opacity: 0.7 }}>ค้างจ่ายรวม</div>
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{data.pendingTopups}</div>
            <div style={{ fontSize: 13, opacity: 0.7 }}>รายการเติมที่รอตรวจ</div>
          </div>
        </div>
      </div>

      {/* --- top-ups awaiting review ------------------------------------- */}
      {data.topups.filter((t) => t.status === 'awaiting_verification' || t.status === 'pending').length > 0 && (
        <div className="admin-group" style={{ marginTop: 12 }}>
          <div className="card-step">รายการเติมเครดิตที่ต้องตรวจ</div>
          {data.topups
            .filter((t) => t.status === 'awaiting_verification' || t.status === 'pending')
            .map((t) => (
              <div key={t.id} className="admin-group" style={{ marginTop: 10 }}>
                <div><strong>{fmt(t.amountDueThb)}</strong> · {TOPUP_STATUS[t.status]} · {t.hasSlip ? 'แนบสลิปแล้ว' : 'ยังไม่แนบสลิป'}</div>
                <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
                  {t.items.map((i) => `${i.tierId} × ${i.quantity}`).join(' + ')}
                  {t.carryoverDiscountThb > 0 && ` · ส่วนลดแลกเครดิต ${fmt(t.carryoverDiscountThb)}`}
                  {' · '}{new Date(t.createdAt).toLocaleString('th-TH')}
                </div>
                <div className="admin-actions" style={{ marginTop: 10 }}>
                  <button
                    className="btn-primary"
                    style={{ padding: '8px 16px' }}
                    disabled={busy}
                    onClick={() => run(() => superadminApi.confirmCreditTopup(token, t.id))}
                  >
                    ยืนยันและเข้าเครดิต
                  </button>
                  <input
                    placeholder="เหตุผลที่ปฏิเสธ"
                    value={rejectReason[t.id] ?? ''}
                    onChange={(e) => setRejectReason((r) => ({ ...r, [t.id]: e.target.value }))}
                  />
                  <button
                    className="btn-link"
                    style={{ color: 'var(--sv-danger)' }}
                    disabled={busy || !(rejectReason[t.id] ?? '').trim()}
                    onClick={() => run(() => superadminApi.rejectCreditTopup(token, t.id, rejectReason[t.id]))}
                  >
                    ปฏิเสธ
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* --- owners ------------------------------------------------------- */}
      <div className="admin-group" style={{ marginTop: 12 }}>
        <div className="card-step">ผู้จัดงาน</div>
        {data.owners.length === 0 && <div style={{ opacity: 0.7 }}>ยังไม่มีผู้จัดงานที่มีเครดิต</div>}
        {data.owners.map((o) => (
          <div key={o.ownerEmail} className="admin-group" style={{ marginTop: 10 }}>
            <button className="btn-link" onClick={() => openOwner(o.ownerEmail)} style={{ textAlign: 'left' }}>
              <strong>{o.ownerEmail || '(ไม่ได้ตั้งอีเมล)'}</strong>
            </button>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
              คงเหลือ {o.spendable.toLocaleString('en-US')} ใบ
              {o.debtCredits > 0 && ` · ค้างจ่าย ${o.debtCredits.toLocaleString('en-US')} ใบ`}
              {o.expiringSoon > 0 && ` · ใกล้หมดอายุใน 30 วัน ${o.expiringSoon.toLocaleString('en-US')} ใบ`}
            </div>

            {selected === o.ownerEmail && (
              <div style={{ marginTop: 12 }}>
                {!detail && <div style={{ opacity: 0.7 }}>กำลังโหลด…</div>}
                {detail && (
                  <>
                    <div className="admin-group">
                      <div className="card-step">ให้เครดิต (ไม่มีการรับเงิน)</div>
                      <div className="ticket-edit-grid" style={{ marginTop: 10 }}>
                        <label>
                          แพ็กเกจ
                          <select value={grant.tierId} onChange={(e) => setGrant((g) => ({ ...g, tierId: e.target.value }))}>
                            <option value="">— กำหนดเอง —</option>
                            {data.tiers.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.credits.toLocaleString('en-US')} เครดิต · {t.validDays} วัน
                              </option>
                            ))}
                          </select>
                        </label>
                        {!grant.tierId && (
                          <>
                            <label>
                              จำนวนเครดิต
                              <input type="number" min={1} value={grant.credits}
                                onChange={(e) => setGrant((g) => ({ ...g, credits: e.target.value }))} />
                            </label>
                            <label>
                              อายุ (วัน)
                              <input type="number" min={1} value={grant.validDays}
                                onChange={(e) => setGrant((g) => ({ ...g, validDays: e.target.value }))} />
                            </label>
                            <label>
                              {/* Required even at zero: reporting has to be able to value the lot. */}
                              ราคาต่อหน่วยสำหรับบันทึกบัญชี (บาท)
                              <input type="number" min={0} value={grant.unitPriceThb}
                                onChange={(e) => setGrant((g) => ({ ...g, unitPriceThb: e.target.value }))} />
                            </label>
                          </>
                        )}
                        <label>
                          เหตุผล (บันทึกลง audit log)
                          <input value={grant.note} onChange={(e) => setGrant((g) => ({ ...g, note: e.target.value }))} />
                        </label>
                      </div>
                      <div className="admin-actions" style={{ marginTop: 12 }}>
                        <button
                          className="btn-primary"
                          style={{ padding: '8px 16px' }}
                          disabled={busy || !grantReady}
                          onClick={() => run(async () => {
                            await superadminApi.grantCredits(token, o.ownerEmail, {
                              ...(grant.tierId ? { tierId: grant.tierId } : {
                                credits: Number(grant.credits),
                                validDays: Number(grant.validDays),
                                unitPriceThb: Number(grant.unitPriceThb) || 0,
                              }),
                              note: grant.note.trim(),
                            });
                            setGrant({ tierId: '', credits: '', validDays: '', unitPriceThb: '', note: '' });
                          })}
                        >
                          ให้เครดิต
                        </button>
                      </div>
                    </div>

                    <div className="admin-group" style={{ marginTop: 12 }}>
                      <div className="card-step">ปรับยอดค้างจ่าย</div>
                      <div style={{ fontSize: 13, opacity: 0.75 }}>
                        ค้างจ่ายปัจจุบัน {detail.debtCredits.toLocaleString('en-US')} ใบ · ใส่ค่าติดลบเพื่อยกหนี้
                      </div>
                      <div className="ticket-edit-grid" style={{ marginTop: 10 }}>
                        <label>
                          จำนวน (+/−)
                          <input type="number" value={debt.delta} onChange={(e) => setDebt((d) => ({ ...d, delta: e.target.value }))} />
                        </label>
                        <label>
                          เหตุผล
                          <input value={debt.reason} onChange={(e) => setDebt((d) => ({ ...d, reason: e.target.value }))} />
                        </label>
                      </div>
                      <div className="admin-actions" style={{ marginTop: 12 }}>
                        <button
                          className="btn-ghost"
                          disabled={busy || !Number(debt.delta) || !debt.reason.trim()}
                          onClick={() => run(async () => {
                            await superadminApi.adjustCreditDebt(token, o.ownerEmail, Number(debt.delta), debt.reason.trim());
                            setDebt({ delta: '', reason: '' });
                          })}
                        >
                          ปรับยอด
                        </button>
                      </div>
                    </div>

                    <div className="admin-group" style={{ marginTop: 12 }}>
                      <div className="card-step">เครดิตแต่ละก้อน</div>
                      {detail.lots.map((l) => (
                        <div key={l.id} style={{ marginTop: 10, fontSize: 13 }}>
                          <strong>{l.creditsRemaining.toLocaleString('en-US')}</strong> / {l.creditsTotal.toLocaleString('en-US')} ใบ
                          {' · '}{l.source}
                          {l.expiresAt
                            ? ` · หมดอายุ ${new Date(l.expiresAt).toLocaleDateString('th-TH')}`
                            : ' · ไม่มีวันหมดอายุ'}
                          {l.closedReason && ` · ปิดแล้ว (${l.closedReason})`}
                          {l.source !== 'free_tier' && (
                            <button
                              className="btn-link"
                              style={{ marginLeft: 10 }}
                              disabled={busy}
                              onClick={() => {
                                const days = window.prompt('ต่ออายุออกไปกี่วันนับจากวันนี้?', '45');
                                if (!days || !Number(days)) return;
                                const reason = window.prompt('เหตุผลในการต่ออายุ');
                                if (!reason?.trim()) return;
                                const expiresAt = new Date(Date.now() + Number(days) * 86_400_000).toISOString();
                                run(() => superadminApi.extendCreditLot(token, l.id, expiresAt, reason.trim()));
                              }}
                            >
                              ต่ออายุ
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="admin-group" style={{ marginTop: 12 }}>
                      <div className="card-step">รายการเดินบัญชีล่าสุด</div>
                      {detail.ledger.slice(0, 40).map((row) => (
                        <div key={row.id} style={{ marginTop: 6, fontSize: 12.5, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                          <span>
                            {row.kind}{row.note && ` — ${row.note}`}
                            <span style={{ opacity: 0.6 }}> · {row.actor}</span>
                          </span>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {row.delta !== 0 && `${row.delta > 0 ? '+' : ''}${row.delta}`}
                            {row.amount_thb > 0 && ` ${fmt(row.amount_thb)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
